import React, { useEffect, useMemo, useState } from "react";
import APP_LOGO from "../assets/nch_logo_512.png";
import { CONTRACT } from "../constants";
import {
  CLAIM_REWARDS_PERMISSION_GUIDE_DOWNLOAD_NAME,
  CLAIM_REWARDS_PERMISSION_GUIDE_PDF_URL,
} from "../constants/permissionGuidePdf";
import { buildClaimRewardsPermissionGuideLines } from "../utils/claimRewardsPermissionGuideContent";
import { buildPermissionGuidePdf, PERMISSION_GUIDE_IMAGE_IDS } from "../utils/permissionGuidePdf";
import { loadImageRgbForPdf } from "../utils/pdfUtils";

const guideBase = `${process.env.PUBLIC_URL || ""}`.replace(/\/$/, "");

/**
 * Final wizard step: inline PDF preview + download for vault claimrewards link-auth.
 */
export default function ClaimRewardsPermissionGuide({
  vaultAccount,
  tokenContract,
  projectName,
}) {
  const guideParams = useMemo(() => {
    const vault = String(vaultAccount || "").trim();
    const token = String(tokenContract || "").trim();
    return {
      contract: CONTRACT,
      vaultAccount: vault && vault !== "—" ? vault : undefined,
      tokenContract: token || undefined,
      projectName: String(projectName || "").trim() || undefined,
    };
  }, [vaultAccount, tokenContract, projectName]);

  const [pdfUrl, setPdfUrl] = useState(CLAIM_REWARDS_PERMISSION_GUIDE_PDF_URL);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    let revoked = false;
    let objectUrl = "";

    async function build() {
      setPdfBusy(true);
      try {
        const lines = buildClaimRewardsPermissionGuideLines(guideParams);
        const headerRightLines = [
          "NFT Claim Hub",
          "Vault claimrewards setup",
          guideParams.vaultAccount ? `Vault: ${guideParams.vaultAccount}` : "",
          guideParams.tokenContract ? `Token: ${guideParams.tokenContract}` : "",
        ].filter(Boolean);

        const logo = await loadImageRgbForPdf(APP_LOGO, 72);
        const images = {};
        await Promise.all(
          PERMISSION_GUIDE_IMAGE_IDS.map(async (id) => {
            const url = `${guideBase}/guide/${id}.png`;
            const img = await loadImageRgbForPdf(url, 480);
            if (img) images[id] = img;
          })
        );

        const bytes = buildPermissionGuidePdf(lines, { logo, images, headerRightLines });
        const blob = new Blob([bytes], { type: "application/pdf" });
        objectUrl = URL.createObjectURL(blob);
        if (!revoked) setPdfUrl(objectUrl);
      } catch {
        if (!revoked) setPdfUrl(CLAIM_REWARDS_PERMISSION_GUIDE_PDF_URL);
      } finally {
        if (!revoked) setPdfBusy(false);
      }
    }

    void build();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [guideParams]);

  const vaultOk = Boolean(guideParams.vaultAccount);
  const tokenOk = Boolean(guideParams.tokenContract);
  const linkTarget = tokenOk ? `${guideParams.tokenContract}::transfer` : "<token>::transfer";

  return (
    <div className="nr-subcard">
      <p className="nr-muted" style={{ fontSize: 14, lineHeight: 1.55, marginBottom: 14 }}>
        This PDF is a step-by-step guide for your <strong>project vault account</strong> (header: <strong>Vault</strong>
        ). NFT Claim Hub pays rewards from that wallet when users claim. You delegate one token{" "}
        <strong>transfer</strong> action to the <strong>{CONTRACT}</strong> smart contract using permission{" "}
        <strong>claimrewards</strong> and <strong>link authority</strong> (<code>linkauth</code>).
      </p>
      <p className="nr-muted" style={{ fontSize: 14, lineHeight: 1.55, marginBottom: 16 }}>
        The PDF includes screenshots for each on-chain step and is rebuilt with your vault and reward token contract
        when available. After setup, explorers should show <code>{CONTRACT} @eosio.code</code> under{" "}
        <code>claimrewards</code> and the linked action <code>{linkTarget}</code>.
      </p>

      {!vaultOk || !tokenOk ? <SetupWarn vaultOk={vaultOk} tokenOk={tokenOk} /> : null}

      <div className="nr-actions" style={{ flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
        <a
          className="nr-btn nr-btnPrimary"
          href={pdfUrl}
          download={CLAIM_REWARDS_PERMISSION_GUIDE_DOWNLOAD_NAME}
          target="_blank"
          rel="noopener noreferrer"
        >
          {pdfBusy ? "Building PDF..." : "Download PDF"}
        </a>
        <a className="nr-btn" href={pdfUrl} target="_blank" rel="noopener noreferrer">
          Open in new tab
        </a>
      </div>
      <div
        className="nr-muted"
        style={{
          fontSize: 12,
          marginBottom: 8,
          borderRadius: 10,
          overflow: "hidden",
          border: "1px solid rgba(148,163,184,0.25)",
          background: "rgba(15,23,42,0.6)",
        }}
      >
        <iframe
          title="NFT Claim Hub claimrewards permission setup guide (PDF)"
          src={pdfUrl}
          style={{ width: "100%", height: "min(72vh, 820px)", border: "none", display: "block" }}
        />
      </div>
    </div>
  );
}

function SetupWarn({ vaultOk, tokenOk }) {
  return (
    <div className="nr-wizard-warn" role="status" style={{ marginBottom: 14, fontSize: 13, lineHeight: 1.5 }}>
      {!vaultOk ? (
        <span>
          Select a <strong>project</strong> with a configured vault so the PDF includes your vault account.{" "}
        </span>
      ) : null}
      {!tokenOk ? (
        <span>
          Select a <strong>pool</strong> with a reward token contract for the correct <code>linkauth</code> target.{" "}
        </span>
      ) : null}
      Until then, placeholder values are used in the personalized PDF.
    </div>
  );
}
